/**
 * Canonical echo measurement catalog (EchoPAC-style).
 *
 * Single source of truth for which parameters the measurement section supports:
 * canonical id, human label, category, UCUM unit, allowed methods, ASE/EACVI
 * reference (normal) ranges, and LOINC code (for the HL7 OBX path — Australia /
 * Medical Outcomes — and FHIR later).
 *
 * Both the viewer-side EchoMeasurementPanel and the report-side EchoMeasurementTable
 * render from this catalog. SR concept-string mapping lives in measurementAliases.ts —
 * extend there, never inline.
 *
 * Reference values: ASE/EACVI chamber-quantification + valvular guidance. Ranges are
 * adult defaults; sex/BSA-specific refinement is a later enhancement (normalRange.indexed
 * flags values that should be body-surface-area indexed).
 */

export type MeasurementCategory = '2D' | 'MMODE' | 'DOPPLER' | 'STRAIN' | 'DERIVED';

export type PatientSex = 'M' | 'F';

/** ASE/EACVI reference (normal) range for a parameter. */
export interface NormalRange {
  low?: number;
  high?: number;
  /** The low cut-off itself is abnormal (guideline states normal as "> low"). */
  lowInclusive?: boolean;
  /** The high cut-off itself is abnormal (guideline states abnormal as "≥ high"). */
  highInclusive?: boolean;
  indexed?: boolean;
}

export interface CatalogEntry {
  measurementId: string; // canonical key
  label: string; // full human label (used in report table / PDF / tooltip)
  shortLabel?: string; // ASE Chamber-Quantification short code shown in the worksheet tree
  category: MeasurementCategory;
  unit: string; // UCUM
  methods?: string[]; // allowed acquisition methods
  planeViews?: string[]; // typical acquisition views
  /** Sex-neutral reference range (fallback when patient sex is unknown, or when the
   *  parameter's ASE range does not differ by sex). */
  normalRange?: NormalRange;
  /** Sex-specific reference ranges (ASE 2015 chamber quantification). When patient sex
   *  is known and a matching band exists, it overrides `normalRange` for abnormal flags. */
  normalRangeBySex?: { M?: NormalRange; F?: NormalRange };
  loincCode?: string;
  /** If true, this value is computed by echoCalc.ts from other primaries, not measured directly. */
  derived?: boolean;
  /** For a derived row the operator can still pick: the measured inputs it is built
   *  from, in capture order. Picking the row arms the first input that has no value. */
  captureVia?: string[];
  /** True when this value must come from a validated external analysis result rather than a
   *  viewer caliper or typed entry. */
  importOnly?: boolean;
  /** This primary can only be entered by the operator; it must not arm a generic
   *  Cornerstone caliper that would create a clinically meaningless value. */
  manualOnly?: boolean;
  /** A persisted safety/workflow control. It may affect a derived result but is never
   *  itself a clinical measurement to include in the signed report. */
  workflowControl?: boolean;
  /** Input presentation for a non-derived primary. Numeric is the default. */
  inputKind?: 'number' | 'checkbox';
  /** For coded/graded derived rows (e.g. the ASE diastolic-function grade): map the
   *  numeric code the engine emits to the label shown in place of the raw number. */
  valueLabels?: Record<number, string>;
}

export const MEASUREMENT_CATALOG: CatalogEntry[] = [
  // ---------------- 2D / linear chamber dimensions ----------------
  {
    measurementId: 'LVIDd',
    label: 'LV internal diameter (diastole)',
    shortLabel: 'LVIDd',
    category: '2D',
    unit: 'cm',
    methods: ['2D', 'M-mode'],
    planeViews: ['PLAX'],
    normalRange: { low: 3.8, high: 5.2 },
    normalRangeBySex: { M: { low: 4.2, high: 5.8 }, F: { low: 3.8, high: 5.2 } },
    loincCode: '29436-3',
  },
  {
    measurementId: 'LVIDs',
    label: 'LV internal diameter (systole)',
    shortLabel: 'LVIDs',
    category: '2D',
    unit: 'cm',
    methods: ['2D', 'M-mode'],
    planeViews: ['PLAX'],
    normalRange: { low: 2.2, high: 3.5 },
    normalRangeBySex: { M: { low: 2.5, high: 4.0 }, F: { low: 2.2, high: 3.5 } },
    loincCode: '29435-5',
  },
  {
    measurementId: 'IVSd',
    label: 'Interventricular septum (diastole)',
    shortLabel: 'IVSd',
    category: '2D',
    unit: 'cm',
    methods: ['2D', 'M-mode'],
    planeViews: ['PLAX'],
    normalRange: { low: 0.6, high: 1.0 },
    normalRangeBySex: { M: { low: 0.6, high: 1.0 }, F: { low: 0.6, high: 0.9 } },
    loincCode: '18156-2',
  },
  {
    measurementId: 'LVPWd',
    label: 'LV posterior wall (diastole)',
    shortLabel: 'LVPWd',
    category: '2D',
    unit: 'cm',
    methods: ['2D', 'M-mode'],
    planeViews: ['PLAX'],
    normalRange: { low: 0.6, high: 1.0 },
    normalRangeBySex: { M: { low: 0.6, high: 1.0 }, F: { low: 0.6, high: 0.9 } },
    loincCode: '18077-0',
  },
  {
    measurementId: 'LA_diam',
    label: 'LA diameter (AP)',
    shortLabel: 'LA diam',
    category: '2D',
    unit: 'cm',
    methods: ['2D', 'M-mode'],
    planeViews: ['PLAX'],
    normalRange: { low: 2.7, high: 4.0 },
    normalRangeBySex: { M: { low: 3.0, high: 4.0 }, F: { low: 2.7, high: 3.8 } },
    loincCode: '18152-1',
  },
  {
    measurementId: 'Ao_root',
    label: 'Aortic root diameter',
    shortLabel: 'Ao root',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PLAX'],
    normalRange: { low: 2.0, high: 3.7 },
  },
  {
    measurementId: 'RV_basal',
    label: 'RV basal diameter',
    shortLabel: 'RV basal',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['A4C'],
    normalRange: { low: 2.5, high: 4.1 },
  },
  // Biplane Simpson — trace LV at end-diastole in BOTH apical views. Each plane
  // trace gives its own single-plane volume; LV_EDV combines them (π/4·h·Σ dA·dB).
  {
    measurementId: 'LV_EDV_A4C',
    label: 'LV EDV trace — A4C',
    shortLabel: 'LVEDV A4C',
    category: '2D',
    unit: 'mL',
    planeViews: ['A4C'],
  },
  {
    measurementId: 'LV_EDV_A2C',
    label: 'LV EDV trace — A2C',
    shortLabel: 'LVEDV A2C',
    category: '2D',
    unit: 'mL',
    planeViews: ['A2C'],
  },
  {
    measurementId: 'LV_EDV',
    label: 'LV end-diastolic volume (Simpson biplane)',
    shortLabel: 'LVEDV',
    category: 'DERIVED',
    unit: 'mL',
    derived: true,
    methods: ['Simpson Biplane', 'Simpson Single Plane'],
    planeViews: ['A4C', 'A2C'],
  },
  {
    measurementId: 'LV_ESV_A4C',
    label: 'LV ESV trace — A4C',
    shortLabel: 'LVESV A4C',
    category: '2D',
    unit: 'mL',
    planeViews: ['A4C'],
  },
  {
    measurementId: 'LV_ESV_A2C',
    label: 'LV ESV trace — A2C',
    shortLabel: 'LVESV A2C',
    category: '2D',
    unit: 'mL',
    planeViews: ['A2C'],
  },
  {
    measurementId: 'LV_ESV',
    label: 'LV end-systolic volume (Simpson biplane)',
    shortLabel: 'LVESV',
    category: 'DERIVED',
    unit: 'mL',
    derived: true,
    methods: ['Simpson Biplane', 'Simpson Single Plane'],
    planeViews: ['A4C', 'A2C'],
  },
  // LA biplane area-length. `LA_area` / `LA_length` ARE the A4C plane — they keep
  // their original ids so studies measured before the A2C rows existed still hydrate.
  {
    measurementId: 'LA_area',
    label: 'LA area — A4C',
    shortLabel: 'LA area A4C',
    category: '2D',
    unit: 'cm2',
    planeViews: ['A4C'],
  },
  {
    measurementId: 'LA_area_A2C',
    label: 'LA area — A2C',
    shortLabel: 'LA area A2C',
    category: '2D',
    unit: 'cm2',
    planeViews: ['A2C'],
  },
  {
    measurementId: 'LA_length',
    label: 'LA length — A4C',
    shortLabel: 'LA length A4C',
    category: '2D',
    unit: 'cm',
    planeViews: ['A4C'],
  },
  {
    measurementId: 'LA_length_A2C',
    label: 'LA length — A2C',
    shortLabel: 'LA length A2C',
    category: '2D',
    unit: 'cm',
    planeViews: ['A2C'],
  },
  {
    measurementId: 'LA_vol',
    label: 'LA volume (biplane area-length)',
    shortLabel: 'LA vol',
    category: 'DERIVED',
    unit: 'mL',
    derived: true,
    methods: ['Area-Length Biplane', 'Area-Length Single Plane'],
    planeViews: ['A4C', 'A2C'],
    captureVia: ['LA_area', 'LA_length', 'LA_area_A2C', 'LA_length_A2C'],
  },

  // ---------------- M-mode ----------------
  // Reported in cm, matching every other length in the catalog (client request,
  // 08 Aug). The ASE cutoffs are therefore 1.7 cm (TAPSE) and 1.1 cm (MAPSE) —
  // the caliper converts to whatever unit the row declares, so nothing else in the
  // capture path needs to know.
  {
    measurementId: 'TAPSE',
    label: 'TAPSE',
    shortLabel: 'TAPSE',
    category: 'MMODE',
    unit: 'cm',
    methods: ['M-mode'],
    planeViews: ['A4C'],
    normalRange: { low: 1.7, lowInclusive: true }, // normal >1.7 cm (ASE right heart 2025)
  },
  {
    measurementId: 'MAPSE',
    label: 'MAPSE',
    shortLabel: 'MAPSE',
    category: 'MMODE',
    unit: 'cm',
    methods: ['M-mode'],
    planeViews: ['A4C'],
  },
  {
    measurementId: 'EPSS',
    label: 'E-point septal separation',
    shortLabel: 'EPSS',
    category: 'MMODE',
    unit: 'cm',
    methods: ['M-mode'],
    planeViews: ['PLAX'],
  },

  // ---------------- Doppler ----------------
  {
    measurementId: 'MV_E',
    label: 'Mitral E velocity',
    shortLabel: 'E vel',
    category: 'DOPPLER',
    unit: 'cm/s',
    methods: ['PW'],
    planeViews: ['A4C'],
  },
  {
    measurementId: 'MV_A',
    label: 'Mitral A velocity',
    shortLabel: 'A vel',
    category: 'DOPPLER',
    unit: 'cm/s',
    methods: ['PW'],
    planeViews: ['A4C'],
  },
  {
    measurementId: 'E_A_ratio',
    label: 'E/A ratio',
    shortLabel: 'E/A',
    category: 'DERIVED',
    unit: '',
    derived: true,
    normalRange: { low: 0.8, high: 2.0 },
  },
  {
    measurementId: 'DT',
    label: 'Deceleration time',
    shortLabel: 'DT',
    category: 'DOPPLER',
    unit: 'ms',
    methods: ['PW'],
    planeViews: ['A4C'],
    normalRange: { low: 160, high: 240 },
  },
  {
    measurementId: 'e_prime_septal',
    label: "e' septal (TDI)",
    shortLabel: "e' sep",
    category: 'DOPPLER',
    unit: 'cm/s',
    methods: ['Tissue Doppler'],
    planeViews: ['A4C'],
    normalRange: { low: 6 }, // septal e' <6 cm/s abnormal irrespective of age (ASE 2025)
  },
  {
    measurementId: 'E_e_prime',
    label: "E/e'",
    shortLabel: "E/e'",
    category: 'DERIVED',
    unit: '',
    derived: true,
    normalRange: { high: 14 },
  },
  {
    measurementId: 'TR_Vmax',
    label: 'TR peak velocity',
    shortLabel: 'TR Vmax',
    category: 'DOPPLER',
    unit: 'm/s',
    methods: ['CW'],
    planeViews: ['A4C', 'RV inflow'],
    normalRange: { high: 2.8, highInclusive: true }, // TR ≥2.8 m/s abnormal (ASE 2025)
  },
  {
    measurementId: 'RVSP',
    label: 'RV systolic pressure',
    shortLabel: 'RVSP',
    category: 'DERIVED',
    unit: 'mmHg',
    derived: true,
    normalRange: { high: 35 },
  },
  // IVC diameter + collapsibility drive the ASE-tiered right-atrial pressure estimate
  // (RAP), which feeds RVSP/PASP instead of the old hardcoded 5 mmHg.
  {
    measurementId: 'IVC_diam',
    label: 'IVC diameter (expiratory)',
    shortLabel: 'IVC diam',
    category: '2D',
    unit: 'cm',
    methods: ['2D', 'M-mode'],
    planeViews: ['Subcostal'],
    normalRange: { high: 2.1 },
  },
  {
    measurementId: 'IVC_collapse',
    label: 'IVC collapsibility index',
    shortLabel: 'IVC collapse',
    category: 'DOPPLER', // measured/typed % (sniff test); grouped with functional indices
    unit: '%',
    methods: ['M-mode', 'Typed'],
    planeViews: ['Subcostal'],
    normalRange: { low: 50 }, // ≥50% collapse = normal RAP (ASE 2025)
    // A percentage needs the largest and smallest IVC diameters at the same site.
    // A generic Doppler/VTI contour cannot provide that safely.
    manualOnly: true,
  },
  {
    measurementId: 'RAP',
    label: 'Right atrial pressure (IVC-estimated)',
    shortLabel: 'RAP',
    category: 'DERIVED',
    unit: 'mmHg',
    derived: true,
    normalRange: { high: 8 },
  },
  {
    measurementId: 'AV_Vmax',
    label: 'Aortic valve peak velocity',
    shortLabel: 'AV Vmax',
    category: 'DOPPLER',
    unit: 'm/s',
    methods: ['CW'],
    planeViews: ['A5C', 'Suprasternal'],
  },
  {
    measurementId: 'AV_peakPG',
    label: 'AV peak gradient',
    shortLabel: 'AV PG max',
    category: 'DERIVED',
    unit: 'mmHg',
    derived: true,
  },
  {
    measurementId: 'AV_meanPG',
    label: 'AV mean gradient',
    shortLabel: 'AV PG mean',
    category: 'DOPPLER',
    unit: 'mmHg',
    methods: ['CW trace'],
    planeViews: ['A5C'],
  },
  {
    measurementId: 'LVOT_VTI',
    label: 'LVOT VTI',
    shortLabel: 'LVOT VTI',
    category: 'DOPPLER',
    unit: 'cm',
    methods: ['PW trace'],
    planeViews: ['A5C'],
  },
  {
    measurementId: 'AVA_continuity',
    label: 'Aortic valve area (continuity)',
    shortLabel: 'AVA',
    category: 'DERIVED',
    unit: 'cm2',
    derived: true,
    normalRange: { low: 2.5, high: 4.5 },
  },
  {
    measurementId: 'DVI',
    label: 'Dimensionless index (LVOT VTI / AV VTI)',
    shortLabel: 'DVI',
    category: 'DERIVED',
    unit: '',
    derived: true,
    // < 0.25 indicates severe aortic stenosis (flow-independent).
    normalRange: { low: 0.25 },
  },
  {
    measurementId: 'aortoseptal_angle',
    label: 'Aorto-septal angle',
    shortLabel: 'Ao-septal ∠',
    category: '2D',
    unit: '°',
    methods: ['Angle'],
    planeViews: ['PLAX'],
    normalRange: { low: 120, high: 150 },
  },
  {
    measurementId: 'MVA_PHT',
    label: 'Mitral valve area (PHT)',
    shortLabel: 'MVA',
    category: 'DERIVED',
    unit: 'cm2',
    derived: true,
    normalRange: { low: 4.0, high: 6.0 },
  },

  // ---------------- Strain (SR import only in phase 1) ----------------
  {
    measurementId: 'GLS',
    label: 'Global longitudinal strain',
    shortLabel: 'GLS',
    category: 'STRAIN',
    unit: '%',
    methods: ['Speckle Tracking (SR import)'],
    planeViews: ['A4C', 'A2C', 'A3C'],
    importOnly: true,
    normalRange: { low: -22, high: -18 },
  },

  // ---------------- Derived / indexed ----------------
  {
    measurementId: 'BSA',
    label: 'Body surface area',
    shortLabel: 'BSA',
    category: 'DERIVED',
    unit: 'm2',
    derived: true,
  },
  {
    measurementId: 'LV_EF',
    label: 'LV ejection fraction (biplane)',
    shortLabel: 'LVEF',
    category: 'DERIVED',
    unit: '%',
    derived: true,
    normalRange: { low: 52, high: 72 },
    normalRangeBySex: { M: { low: 52, high: 72 }, F: { low: 54, high: 74 } },
    loincCode: '10230-1',
  },
  {
    measurementId: 'LV_mass',
    label: 'LV mass (ASE cube)',
    shortLabel: 'LV mass',
    category: 'DERIVED',
    unit: 'g',
    derived: true,
  },
  {
    measurementId: 'LVMI',
    label: 'LV mass index',
    shortLabel: 'LVMi',
    category: 'DERIVED',
    unit: 'g/m2',
    derived: true,
    normalRange: { indexed: true, high: 95 },
    normalRangeBySex: {
      M: { indexed: true, low: 49, high: 115 },
      F: { indexed: true, low: 43, high: 95 },
    },
  },
  {
    measurementId: 'FS',
    label: 'Fractional shortening',
    shortLabel: 'FS',
    category: 'DERIVED',
    unit: '%',
    derived: true,
    normalRange: { low: 25, high: 45 },
    normalRangeBySex: { M: { low: 25, high: 43 }, F: { low: 27, high: 45 } }, // ASE 2005
  },
  {
    measurementId: 'RV_FAC',
    label: 'RV fractional area change',
    shortLabel: 'RV FAC',
    category: 'DERIVED',
    unit: '%',
    derived: true,
    normalRange: { low: 35, lowInclusive: true }, // normal >35 % (ASE right heart 2025)
  },
  {
    measurementId: 'RWT',
    label: 'Relative wall thickness',
    shortLabel: 'RWT',
    category: 'DERIVED',
    unit: '',
    derived: true,
    // > 0.42 = concentric remodelling / hypertrophy.
    normalRange: { high: 0.42 },
  },
  {
    measurementId: 'LAVi',
    label: 'LA volume index',
    shortLabel: 'LAVi',
    category: 'DERIVED',
    unit: 'mL/m2',
    derived: true,
    normalRange: { indexed: true, high: 34 },
  },
  {
    measurementId: 'LVEDVI',
    label: 'LV end-diastolic volume index',
    shortLabel: 'LVEDVi',
    category: 'DERIVED',
    unit: 'mL/m2',
    derived: true,
    // ASE 2015 Supplemental Table 3, same bands the report wording engine grades
    // (wordingRules.ts lvSize): male 34–74, female 29–61 mL/m².
    normalRange: { indexed: true, low: 34, high: 74 },
    normalRangeBySex: {
      M: { indexed: true, low: 34, high: 74 },
      F: { indexed: true, low: 29, high: 61 },
    },
  },
  {
    measurementId: 'LVESVI',
    label: 'LV end-systolic volume index',
    shortLabel: 'LVESVi',
    category: 'DERIVED',
    unit: 'mL/m2',
    derived: true,
    normalRange: { indexed: true, low: 11, high: 31 },
    normalRangeBySex: {
      M: { indexed: true, low: 11, high: 31 },
      F: { indexed: true, low: 8, high: 24 },
    },
  },
  {
    measurementId: 'SV',
    label: 'Stroke volume',
    shortLabel: 'SV',
    category: 'DERIVED',
    unit: 'mL',
    derived: true,
  },
  {
    measurementId: 'CO',
    label: 'Cardiac output',
    shortLabel: 'CO',
    category: 'DERIVED',
    unit: 'L/min',
    derived: true,
  },

  // ---------------- EchoPAC parity — added dimensions (25 Jun) ----------------
  // Linear dimensions EchoPAC lists separately under Cardiac → Dimension.
  {
    measurementId: 'Basal_IVS',
    label: 'Basal interventricular septum',
    shortLabel: 'IVSd Basal',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PLAX'],
    normalRange: { low: 0.6, high: 1.0 },
  },
  {
    measurementId: 'LVOT_diam',
    label: 'LVOT diameter',
    shortLabel: 'LVOT diam',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PLAX'],
    normalRange: { low: 1.8, high: 2.5 },
  },
  {
    measurementId: 'Ao_sinus',
    label: 'Aortic sinus of Valsalva diameter',
    shortLabel: 'Ao sinus',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PLAX'],
    normalRange: { low: 2.9, high: 4.0 },
  },
  {
    measurementId: 'Ao_STJ',
    label: 'Sinotubular junction diameter',
    shortLabel: 'Ao STJ',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PLAX'],
    normalRange: { high: 3.6 },
  },
  {
    measurementId: 'Ao_asc',
    label: 'Ascending aorta diameter',
    shortLabel: 'Ao asc',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PLAX'],
    normalRange: { high: 3.7 },
  },
  {
    measurementId: 'Ao_arch',
    label: 'Aortic arch diameter',
    shortLabel: 'Ao arch',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['Suprasternal'],
  },
  {
    measurementId: 'RVOT_diam',
    label: 'RVOT diameter (proximal)',
    shortLabel: 'RVOT diam',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PSAX'],
    normalRange: { high: 3.4, highInclusive: true }, // normal <3.4 cm (ASE right heart 2025)
  },
  {
    measurementId: 'RV_mid',
    label: 'RV mid-cavity diameter',
    shortLabel: 'RV mid',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['A4C'],
    normalRange: { high: 3.5 },
  },
  {
    measurementId: 'RV_length',
    label: 'RV longitudinal length',
    shortLabel: 'RV length',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['A4C'],
    normalRange: { high: 8.2, highInclusive: true }, // normal <8.2 cm (ASE right heart 2025)
  },
  {
    measurementId: 'PA',
    label: 'Main pulmonary artery diameter',
    shortLabel: 'PA',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PSAX'],
    normalRange: { high: 2.9 },
  },
  {
    measurementId: 'LPA',
    label: 'Left pulmonary artery diameter',
    shortLabel: 'LPA',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PSAX'],
  },
  {
    measurementId: 'RPA',
    label: 'Right pulmonary artery diameter',
    shortLabel: 'RPA',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PSAX'],
  },
  {
    measurementId: 'AV_annulus',
    label: 'Aortic valve annulus diameter',
    shortLabel: 'AV annulus',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PLAX'],
  },
  {
    measurementId: 'MV_annulus',
    label: 'Mitral valve annulus diameter',
    shortLabel: 'MV annulus',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['A4C'],
  },
  {
    measurementId: 'TV_annulus',
    label: 'Tricuspid valve annulus diameter',
    shortLabel: 'TV annulus',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['A4C'],
  },
  {
    measurementId: 'PV_annulus',
    label: 'Pulmonary valve annulus diameter',
    shortLabel: 'PV annulus',
    category: '2D',
    unit: 'cm',
    methods: ['2D'],
    planeViews: ['PSAX'],
  },
  {
    measurementId: 'RA_area',
    label: 'RA area (A4C)',
    shortLabel: 'RA area',
    category: '2D',
    unit: 'cm2',
    methods: ['2D'],
    planeViews: ['A4C'],
    normalRange: { high: 19, highInclusive: true }, // normal <19 cm² (ASE right heart 2025)
  },
  // RV: captured as areas (not the RV-basal/mid rectangular proxies SH) so FAC
  // is derivable. ED/ES timing and the A4C view are the same as for LA.
  {
    measurementId: 'RV_EDA',
    label: 'RV end-diastolic area',
    shortLabel: 'RV EDA',
    category: '2D',
    unit: 'cm2',
    methods: ['2D'],
    planeViews: ['A4C'],
  },
  {
    measurementId: 'RV_ESA',
    label: 'RV end-systolic area',
    shortLabel: 'RV ESA',
    category: '2D',
    unit: 'cm2',
    methods: ['2D'],
    planeViews: ['A4C'],
  },
  // RA: AREA ONLY — Dr Shashi (30 Jun): "for RA we only do the area, no one does RA
  // volume." RA volume / RA volume-index / RA length rows removed deliberately.

  // EchoPAC parity — added Doppler / tissue-Doppler (25 Jun)
  {
    measurementId: 'RVOT_VTI',
    label: 'RVOT VTI',
    shortLabel: 'RVOT VTI',
    category: 'DOPPLER',
    unit: 'cm',
    methods: ['PW trace'],
    planeViews: ['PSAX'],
  },
  // CW across the valve (BSE minimum dataset 2020; EAE/ASE valve stenosis 2009 §V.B.1.1).
  // PW in the RVOT is RVOT velocity, a different value. No guideline normal range found.
  {
    measurementId: 'PV_Vmax',
    label: 'Pulmonary valve peak velocity',
    shortLabel: 'PV Vmax',
    category: 'DOPPLER',
    unit: 'm/s',
    methods: ['CW'],
    planeViews: ['PSAX', 'Subcostal'],
  },
  {
    measurementId: 'PV_accelTime',
    label: 'Pulmonary valve acceleration time',
    shortLabel: 'PV AccT',
    category: 'DOPPLER',
    unit: 'ms',
    methods: ['PW'],
    planeViews: ['PSAX'],
    normalRange: { low: 105, lowInclusive: true }, // normal >105 ms (ASE right heart 2025)
  },
  {
    measurementId: 'S_prime',
    label: "S' (RV systolic tissue velocity)",
    shortLabel: "S'",
    category: 'DOPPLER',
    unit: 'cm/s',
    methods: ['Tissue Doppler'],
    planeViews: ['A4C'],
    normalRange: { low: 9.5 },
  },
  {
    measurementId: 'e_prime_lateral',
    label: "e' lateral (TDI)",
    shortLabel: "e' lat",
    category: 'DOPPLER',
    unit: 'cm/s',
    methods: ['Tissue Doppler'],
    planeViews: ['A4C'],
    normalRange: { low: 7 }, // lateral e' <7 cm/s abnormal irrespective of age (ASE 2025)
  },
  {
    measurementId: 'QP_QS',
    label: 'Qp/Qs (pulmonary:systemic flow)',
    shortLabel: 'Qp/Qs',
    category: 'DERIVED',
    unit: '',
    derived: true,
    methods: ['Doppler (RVOT/LVOT)'],
    normalRange: { low: 0.8, high: 1.2 }, // ≥1.5 = significant L→R shunt
  },

  // EchoPAC parity — inputs that drive the derived engine (25 Jun)
  // Patient biometrics (typed) → BSA → all indexed values (LVMI / LAVi / RAVi).
  {
    measurementId: 'HT',
    label: 'Height',
    shortLabel: 'Height',
    category: 'DERIVED', // typed input row (derived:false); grouped under Patient
    unit: 'cm',
    methods: ['Typed'],
  },
  {
    measurementId: 'WT',
    label: 'Weight',
    shortLabel: 'Weight',
    category: 'DERIVED',
    unit: 'kg',
    methods: ['Typed'],
  },
  {
    measurementId: 'HR',
    label: 'Heart rate',
    shortLabel: 'HR',
    category: 'DERIVED',
    unit: 'bpm',
    methods: ['Typed'],
    normalRange: { low: 60, high: 100 },
  },
  {
    measurementId: 'DIASTOLIC_GENERAL_PATHWAY_CONFIRMED',
    label: 'General diastolic pathway applicable — sinus rhythm, no special population',
    shortLabel: 'General DF path',
    // This is a persisted workflow signal rather than a clinical measurement. The
    // workspace groups it with the other patient/context inputs below.
    category: 'DERIVED',
    unit: '',
    methods: ['Clinician confirmation'],
    workflowControl: true,
    inputKind: 'checkbox',
  },
  // AV VTI (CW) → aortic valve area by continuity; Mitral PHT → mitral valve area.
  {
    measurementId: 'AV_VTI',
    label: 'Aortic valve VTI',
    shortLabel: 'AV VTI',
    category: 'DOPPLER',
    unit: 'cm',
    methods: ['CW trace'],
    planeViews: ['A5C'],
  },
  {
    measurementId: 'MV_PHT',
    label: 'Mitral pressure half-time',
    shortLabel: 'MV PHT',
    category: 'DOPPLER',
    unit: 'ms',
    methods: ['CW'],
    planeViews: ['A4C'],
  },
  // ASE 2025 diastolic supplemental inputs (LA pressure markers)
  {
    measurementId: 'LA_reservoir_strain',
    label: 'LA reservoir strain (LARS)',
    shortLabel: 'LARS',
    category: 'STRAIN',
    unit: '%',
    methods: ['Speckle Tracking'],
    planeViews: ['A4C', 'A2C'],
    importOnly: true,
    normalRange: { low: 18, lowInclusive: true }, // LARS ≤18% = elevated LA pressure marker (ASE 2025)
  },
  {
    measurementId: 'PV_SD_ratio',
    label: 'Pulmonary vein S/D ratio',
    shortLabel: 'PV S/D',
    category: 'DOPPLER',
    unit: '',
    methods: ['PW'],
    planeViews: ['A4C'],
    normalRange: { low: 0.67, lowInclusive: true }, // PV S/D ≤0.67 = elevated LA pressure marker (ASE 2025)
  },

  // ASE — pulmonary pressure, global function & diastolic grading (30 Jul)
  {
    measurementId: 'mPAP',
    label: 'Mean pulmonary artery pressure',
    shortLabel: 'mPAP',
    category: 'DERIVED',
    unit: 'mmHg',
    derived: true,
    methods: ['PV AccT (Mahan / Kitabatake)'],
    normalRange: { high: 20 }, // >20 mmHg = pulmonary hypertension (2022 ESC/ERS)
  },
  {
    measurementId: 'dPdt_time',
    label: 'MR dP/dt interval (1→3 m/s)',
    shortLabel: 'dP/dt Δt',
    category: 'DOPPLER',
    unit: 'ms',
    methods: ['CW (MR jet)'],
    planeViews: ['A4C'],
  },
  {
    measurementId: 'LV_dPdt',
    label: 'LV dP/dt',
    shortLabel: 'dP/dt',
    category: 'DERIVED',
    unit: 'mmHg/s',
    derived: true,
  },
  {
    measurementId: 'IVCT',
    label: 'Isovolumic contraction time',
    shortLabel: 'IVCT',
    category: 'DOPPLER',
    unit: 'ms',
    methods: ['Tissue Doppler'],
    planeViews: ['A4C'],
  },
  {
    measurementId: 'IVRT',
    label: 'Isovolumic relaxation time',
    shortLabel: 'IVRT',
    category: 'DOPPLER',
    unit: 'ms',
    methods: ['PW / Tissue Doppler'],
    planeViews: ['A4C', 'A5C'],
    normalRange: { low: 50, high: 100 },
  },
  {
    measurementId: 'ET',
    label: 'Ejection time',
    shortLabel: 'ET',
    category: 'DOPPLER',
    unit: 'ms',
    methods: ['PW (LVOT / RVOT)'],
    planeViews: ['A5C', 'PSAX'],
  },
  {
    measurementId: 'Tei',
    label: 'Tei index (myocardial performance)',
    shortLabel: 'Tei / MPI',
    category: 'DERIVED',
    unit: '',
    derived: true,
  },
  {
    measurementId: 'DIAST_GRADE',
    label: 'Diastolic function (ASE 2025)',
    shortLabel: 'Diastolic fn',
    category: 'DERIVED',
    unit: '',
    derived: true,
    normalRange: { high: 0 }, // any grade ≥ I flags abnormal
    valueLabels: { 0: 'Normal', 1: 'Grade I', 2: 'Grade II', 3: 'Grade III', 9: 'Indeterminate' },
  },
  {
    measurementId: 'TAPSE_grade',
    label: 'RV systolic function (TAPSE)',
    shortLabel: 'RV sys fn',
    category: 'DERIVED',
    unit: '',
    derived: true,
    normalRange: { high: 0 }, // "Reduced" (code 1) flags abnormal
    valueLabels: { 0: 'Normal', 1: 'Reduced' }, // ASE: TAPSE <17 mm = RV systolic dysfunction
  },
];

/** Fast lookup by canonical measurementId. */
export const CATALOG_BY_ID: Record<string, CatalogEntry> = MEASUREMENT_CATALOG.reduce(
  (acc, entry) => {
    acc[entry.measurementId] = entry;
    return acc;
  },
  {} as Record<string, CatalogEntry>
);

/** Entries grouped by category, for rendering the panel/table in EchoPAC-style sections. */
export const CATALOG_BY_CATEGORY: Record<MeasurementCategory, CatalogEntry[]> =
  MEASUREMENT_CATALOG.reduce(
    (acc, entry) => {
      (acc[entry.category] ||= []).push(entry);
      return acc;
    },
    {} as Record<MeasurementCategory, CatalogEntry[]>
  );

/**
 * True when a measured value falls outside the catalog reference range.
 *
 * When patient `sex` is provided and the parameter carries a sex-specific band
 * (`normalRangeBySex`), that band is used — otherwise the sex-neutral `normalRange`.
 * Passing no sex preserves the original sex-neutral behaviour (backward compatible).
 */
/**
 * Coerce whatever a source hands us into a catalog sex, or null.
 *
 * Sources disagree: DICOM `PatientSex` is 'M'/'F'/'O', the report record stores free-ish
 * text ('Male', 'female', 'M'), and either can be absent. Every surface that flags
 * abnormals must normalise the same way, otherwise one screen applies the sex-specific
 * ASE band and another silently falls back to the pooled one for the same patient.
 * Anything not clearly male or female returns null → pooled range, which is the safe
 * default rather than a guess.
 */
export function normalizePatientSex(raw: unknown): PatientSex | null {
  const first = String(raw ?? '')
    .trim()
    .toUpperCase()
    .charAt(0);
  return first === 'M' || first === 'F' ? (first as PatientSex) : null;
}

/** The reference band to display for a parameter, honouring sex when we know it. */
export function normalRangeFor(measurementId: string, sex?: PatientSex | null) {
  const entry = CATALOG_BY_ID[measurementId];
  if (!entry) return undefined;
  return (sex && entry.normalRangeBySex?.[sex]) || entry.normalRange;
}

export function isAbnormal(
  measurementId: string,
  value: number | null | undefined,
  sex?: PatientSex | null
): boolean {
  if (value === null || value === undefined) return false;
  const entry = CATALOG_BY_ID[measurementId];
  if (!entry) return false;
  const range = (sex && entry.normalRangeBySex?.[sex]) || entry.normalRange;
  if (!range) return false;
  if (range.low !== undefined && (value < range.low || (range.lowInclusive && value === range.low)))
    return true;
  if (range.high !== undefined && (value > range.high || (range.highInclusive && value === range.high)))
    return true;
  return false;
}
